/**
 * Fuente única de entitlements en UI — reflejo de GET /system/info → module_permissions.
 * v1/v2 resueltos en backend (_owner_active_modules + parse_license_entitlements).
 */
import { defaultEntitlements, isEntitlementActive } from "@/modules/catalogModulos";

/** Rutas del sidebar Visor que requieren entitlement «visor» (alineado con _visor_module_gate). */
export const VISOR_GATED_NAV_PREFIXES = [
  "/carga-informacion",
  "/descarga-automatica-sat",
  "/documentos-fiscales-sat",
  "/monitor-sincronizacion-sat",
  "/contabilidad",
  "/mis-cfdi",
  "/gestionar-xml",
  "/conciliacion-manual-cfdi",
  "/visor",
  "/visores-sat",
  "/confronta-cfdi",
  "/confronta",
  "/auditoria-efos",
  "/auditoria-estatus-local",
  "/exportacion-local",
];

/**
 * @deprecated Fase 1 — usar usePlatformDesigner() / user.is_platform_designer del backend.
 * TODO FASE 3: usar useCapabilities().isPlatformDesigner.
 */
export function resolveDesignerUnlock(user) {
  return Boolean(user?.is_platform_designer);
}

/**
 * Normaliza module_permissions del servidor a entitlements + flags legacy (mirror backend).
 */
export function entitlementsFromSystemInfo(systemInfo) {
  if (!systemInfo) {
    return {
      entitlements: defaultEntitlements(),
      modulo_visor_sat: true,
      modulo_asistente_laboral: false,
      modulo_tramites_fiscales: true,
      modulo_gestion_despacho: false,
      source: "loading",
    };
  }

  const perms = systemInfo.module_permissions || {};
  const entitlements =
    perms.entitlements && typeof perms.entitlements === "object"
      ? { ...defaultEntitlements(), ...perms.entitlements }
      : defaultEntitlements();

  const ctx = { entitlements, modulePermissions: perms };

  return {
    entitlements,
    modulo_visor_sat: isEntitlementActive("visor", ctx),
    modulo_asistente_laboral: isEntitlementActive("laboral", ctx),
    modulo_tramites_fiscales: isEntitlementActive("tramites", ctx),
    modulo_gestion_despacho: isEntitlementActive("despacho", ctx),
    source: perms.source || (systemInfo.license?.present ? "license" : "installation"),
    catalog: systemInfo.module_catalog || null,
  };
}

export function hasEntitlement(entitlementId, entitlementsState, { isPlatformDesigner = false } = {}) {
  if (isPlatformDesigner) return true;
  if (!entitlementsState) return false;
  return isEntitlementActive(entitlementId, {
    entitlements: entitlementsState.entitlements,
    modulePermissions: {
      modulo_visor_sat: entitlementsState.modulo_visor_sat,
      modulo_asistente_laboral: entitlementsState.modulo_asistente_laboral,
      modulo_tramites_fiscales: entitlementsState.modulo_tramites_fiscales,
      modulo_gestion_despacho: entitlementsState.modulo_gestion_despacho,
    },
  });
}

export function isVisorNavPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return VISOR_GATED_NAV_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Rutas Laboral operativas en sidebar (excluye hub /laboral/inicio). */
export const LABORAL_GATED_NAV_PATHS = new Set([
  "/clientes",
  "/registros-patronales-certificados",
  "/catalogo-trabajadores",
  "/expedientes-laborales",
  "/control-repse",
  "/confronta-trabajadores-imss",
  "/confronta-emisiones-imss",
]);

/** Rutas Trámites operativas en sidebar (excluye hub /tramites/inicio). */
export const TRAMITES_GATED_NAV_PREFIXES = [
  "/tramites-fiscales",
  "/tramites/validacion-rfc-sat",
  "/tramites/inscripcion-trabajadores-sat",
  "/tramites/inscripcion-trabajadores-imss",
  "/validador-rfc-sat",
  "/validador-nss-imss",
  "/inscripcion-trabajadores-sat",
  "/inscripcion-trabajadores-imss",
  "/validacion-rfc-sat",
  "/asistente-validacion-sat",
];

export function isLaboralNavPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return LABORAL_GATED_NAV_PATHS.has(path);
}

export function isTramitesNavPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  if (path === "/tramites/inicio") return false;
  return TRAMITES_GATED_NAV_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  ) || path === "/tramites-fiscales";
}

/** Rutas Despacho operativas en sidebar (excluye hub /despacho/inicio). */
export function isDespachoNavPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  if (path === "/despacho/inicio") return false;
  return path === "/despacho" || path.startsWith("/despacho/");
}

/** Rutas Materialidad operativas (excluye hub /materialidad/inicio). */
export function isMaterialidadNavPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  if (path === "/materialidad/inicio") return false;
  return path === "/materialidad" || path.startsWith("/materialidad/");
}

export function filterNavByEntitlements(
  navItems,
  {
    isPlatformDesigner = false,
    entitlementsLoading = false,
    entitlementsState,
    activeWorkModule = "visor_sat",
  } = {},
) {
  if (isPlatformDesigner || entitlementsLoading) {
    return navItems || [];
  }

  const items = navItems || [];
  const moduleId = String(activeWorkModule || "visor_sat");

  if (moduleId === "visor_sat") {
    if (hasEntitlement("visor", entitlementsState, { isPlatformDesigner })) {
      return items;
    }
    return items.filter((item) => !isVisorNavPath(item.to));
  }

  if (moduleId === "asistente_laboral") {
    if (hasEntitlement("laboral", entitlementsState, { isPlatformDesigner })) {
      return items;
    }
    return items.filter((item) => !isLaboralNavPath(item.to));
  }

  if (moduleId === "tramites_fiscales") {
    if (hasEntitlement("tramites", entitlementsState, { isPlatformDesigner })) {
      return items;
    }
    return items.filter((item) => !isTramitesNavPath(item.to));
  }

  if (moduleId === "gestion_despacho") {
    if (hasEntitlement("despacho", entitlementsState, { isPlatformDesigner })) {
      return items;
    }
    return items.filter((item) => !isDespachoNavPath(item.to));
  }

  if (moduleId === "materialidad") {
    if (hasEntitlement("tramites", entitlementsState, { isPlatformDesigner })) {
      return items;
    }
    return items.filter((item) => !isMaterialidadNavPath(item.to));
  }

  return items;
}
